import { Inject, Injectable } from '@angular/core';
import { AuthService } from '@principle-theorem/ng-auth';
import {
  INgPrincipleConfig,
  NG_PRINCIPLE_CONFIG,
} from '@principle-theorem/ng-principle';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { IPractice } from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  snapshot,
  snapshotDefined,
} from '@principle-theorem/shared';

interface IOAuthRequest {
  organisationId: string;
  practiceIds: string[];
  response_type: 'code';
  client_id: string;
  redirect_uri: string;
  scope: string;
  state: string;
  [key: string]: string | string[];
}

interface IOAuthResponse {
  redirect_uri: string;
  code: string;
}

@Injectable()
export class OAuthService {
  constructor(
    @Inject(NG_PRINCIPLE_CONFIG) private _config: INgPrincipleConfig,
    private _auth: AuthService,
    private _organisation: OrganisationService
  ) {}

  async confirmConsent(
    practiceRefs: DocumentReference<IPractice>[],
    clientId: string,
    redirectUri: string,
    scope: string[],
    state: string
  ): Promise<{ redirectUri?: string; code?: string }> {
    const organisation = await snapshotDefined(
      this._organisation.organisation$
    );

    const request: IOAuthRequest = {
      response_type: 'code',
      client_id: clientId,
      redirect_uri: redirectUri,
      scope: scope.join(' '),
      state,
      organisationId: organisation.ref.id,
      practiceIds: practiceRefs.map((ref) => ref.id),
    };

    const query = new URLSearchParams(
      request as string[][] | Record<string, string> | string | URLSearchParams
    ).toString();

    const token = await snapshot(this._auth.authToken$);
    if (!token) {
      throw new Error('User is not authenticated');
    }

    const response = await fetch(
      `${this._config.apiDocsUrl}/v1/oauth/authorize?${query}`,
      {
        headers: {
          Authorization: `Bearer ${token.token}`,
        },
      }
    );
    const data = (await response.json()) as IOAuthResponse;

    return {
      redirectUri: data.redirect_uri,
      code: data.code,
    };
  }
}
